import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Trophy, TrendingUp, Users, Bookmark, ShoppingCart, Activity,
  Crown, Medal, Phone, Video, MessageCircle, Mail, MapPin, Loader2,
  X, Calendar, DollarSign, Target, Star, ChevronRight, Gauge, Clock,
  LineChart, Megaphone, Building2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useDashboardRealTime } from '../hooks/useWebSocket';
import { TrendsChart, ConversionFunnel, LeadsBySource, ComparisonCard, TopBrokersList } from '../components/DashboardEnhanced';

const activityIcons = {
  llamada: Phone,
  whatsapp: MessageCircle,
  email: Mail,
  zoom: Video,
  visita: MapPin,
  apartado: Bookmark,
  venta: ShoppingCart,
  nota: Activity,
};

const DASHBOARD_RELOAD_EVENTS = new Set([
  'lead_created',
  'lead_updated',
  'lead_deleted',
  'leads_bulk_updated',
  'leads_bulk_deleted',
  'import_completed',
]);

// Clickable Stat Card
const StatCard = ({ title, value, goal, icon: Icon, color, progress, onClick, clickable = true }) => (
  <Card 
    className={`relative overflow-hidden group hover:shadow-lg transition-all ${clickable ? 'cursor-pointer hover:scale-[1.02]' : ''}`}
    onClick={clickable ? onClick : undefined}
  >
    <CardContent className="p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 sm:space-y-2 min-w-0 flex-1">
          <p className="text-xs sm:text-sm text-muted-foreground truncate">{title}</p>
          <div className="flex items-baseline gap-1 sm:gap-2 flex-wrap">
            <h3 className="text-2xl sm:text-3xl font-bold font-['Outfit']">{value}</h3>
            {goal && (
              <span className="text-xs sm:text-sm text-muted-foreground">/ {goal}</span>
            )}
          </div>
          {progress !== undefined && (
            <Progress value={Math.min(progress, 100)} className="h-1.5 w-full sm:w-24" />
          )}
        </div>
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
      </div>
      {progress !== undefined && (
        <p className={`text-xs mt-2 ${progress >= 100 ? 'text-primary' : 'text-muted-foreground'}`}>
          {progress >= 100 ? '¡Meta alcanzada!' : `${Math.round(progress)}% completado`}
        </p>
      )}
      {clickable && (
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      )}
    </CardContent>
  </Card>
);

const formatCurrency = (value = 0) => new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 0,
}).format(Number(value) || 0);

const ExecutiveMetricCard = ({ title, value, helper, icon: Icon, tone = 'primary' }) => {
  const toneClasses = {
    primary: 'bg-primary/10 text-primary',
    emerald: 'bg-emerald-500/10 text-emerald-600',
    amber: 'bg-amber-500/10 text-amber-600',
    blue: 'bg-blue-500/10 text-blue-600',
    violet: 'bg-violet-500/10 text-violet-600',
  };

  return (
    <Card>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="mt-1 text-xl sm:text-2xl font-bold font-['Outfit']">{value}</p>
            {helper && <p className="mt-1 text-xs text-muted-foreground">{helper}</p>}
          </div>
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${toneClasses[tone] || toneClasses.primary}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AgencyExecutiveDashboard = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-72" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((item) => <Skeleton key={item} className="h-28 w-full" />)}
        </div>
      </div>
    );
  }

  const overview = data?.overview || {};
  const team = data?.team_performance || [];
  const marketing = data?.marketing_roi || [];
  const properties = data?.top_properties || [];
  const timeline = data?.timeline || [];
  const maxTimelineLeads = Math.max(...timeline.map((item) => item.leads || 0), 1);

  return (
    <section className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold font-['Outfit'] flex items-center gap-2">
          <LineChart className="w-5 h-5 text-primary" />
          Panel Ejecutivo de Conversión y Ventas
        </h2>
        <p className="text-sm text-muted-foreground">Salud comercial, equipo, marketing y comportamiento temporal del negocio.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <ExecutiveMetricCard title="Prospectos Totales" value={overview.total_prospects || 0} helper={`${overview.qualified_leads || 0} qualified leads`} icon={Users} tone="blue" />
        <ExecutiveMetricCard title="Oportunidades Reales" value={overview.opportunities || 0} helper="Presentación, apartado o venta" icon={Target} tone="violet" />
        <ExecutiveMetricCard title="Ingresos Esperados" value={formatCurrency(overview.expected_revenue)} helper="Pipeline en etapas finales" icon={DollarSign} tone="emerald" />
        <ExecutiveMetricCard title="Sales Velocity" value={`${overview.sales_velocity_days || 0} días`} helper={`${overview.conversion_rate || 0}% conversión global`} icon={Gauge} tone="amber" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
        <Card className="xl:col-span-2">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              Rendimiento del Equipo y Brokers
            </CardTitle>
            <CardDescription>Leads asignados, conversión individual, ventas, apartados y puntos.</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="space-y-3">
              {team.slice(0, 6).map((broker, index) => (
                <div key={broker.broker_id || index} className="grid grid-cols-[1fr,auto] gap-3 rounded-xl border border-border/70 p-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant={index < 3 ? 'default' : 'secondary'}>#{index + 1}</Badge>
                      <p className="font-medium truncate">{broker.broker_name}</p>
                    </div>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-muted-foreground">
                      <span>{broker.leads_assigned || 0} leads</span>
                      <span>{broker.conversion_rate || 0}% conv.</span>
                      <span>{broker.ventas || 0} ventas</span>
                      <span>{broker.apartados || 0} apartados</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">{broker.points || 0}</p>
                    <p className="text-xs text-muted-foreground">pts</p>
                  </div>
                </div>
              ))}
              {team.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Aún no hay brokers con métricas.</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" />
              ROI por Fuente
            </CardTitle>
            <CardDescription>CPL, CPA y retorno por canal.</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="space-y-3">
              {marketing.slice(0, 5).map((source) => (
                <div key={source.source} className="rounded-xl bg-muted/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-sm truncate">{source.source}</p>
                    <Badge variant={source.roi > 0 ? 'default' : 'secondary'}>{source.roi || 0}% ROI</Badge>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                    <span>CPL {formatCurrency(source.cpl)}</span>
                    <span>CPA {formatCurrency(source.cpa)}</span>
                    <span>{source.leads || 0} leads</span>
                  </div>
                </div>
              ))}
              {marketing.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No hay datos de fuentes todavía.</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Desempeño del Catálogo
            </CardTitle>
            <CardDescription>Propiedades más cotizadas o visitadas por clientes.</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="space-y-3">
              {properties.slice(0, 6).map((property, index) => (
                <div key={property.product_id || property.title || index} className="flex items-center justify-between gap-3 rounded-xl border border-border/70 p-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{property.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {property.quoted_leads || 0} cotizaciones · {property.ventas || 0} ventas
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{property.commission_percentage || 0}%</p>
                    <p className="text-xs text-muted-foreground">comisión</p>
                  </div>
                </div>
              ))}
              {properties.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Sin interés de propiedades registrado aún.</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Timeline Analysis
            </CardTitle>
            <CardDescription>Picos semanales de leads, oportunidades y ventas.</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="space-y-3">
              {timeline.map((item) => (
                <div key={item.period} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">Semana {item.period}</span>
                    <span className="text-muted-foreground">{item.leads || 0} leads · {item.ventas || 0} ventas</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${((item.leads || 0) / maxTimelineLeads) * 100}%` }} />
                  </div>
                </div>
              ))}
              {timeline.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No hay línea de tiempo suficiente todavía.</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

const BrokerPerformanceDashboard = ({ data, loading, onMetricClick }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-72" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((item) => <Skeleton key={item} className="h-28 w-full" />)}
        </div>
      </div>
    );
  }

  const performance = data?.performance || {};
  const pipeline = data?.pipeline || {};
  const feed = data?.activity_feed || [];
  const funnelStages = [
    ['nuevo', 'Nuevo'],
    ['contactado', 'Contacto'],
    ['calificacion', 'Calificación'],
    ['presentacion', 'Presentación'],
    ['apartado', 'Apartado'],
    ['venta', 'Venta'],
  ];
  const maxStage = Math.max(...funnelStages.map(([key]) => pipeline[key] || 0), 1);

  return (
    <section className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold font-['Outfit'] flex items-center gap-2">
          <Gauge className="w-5 h-5 text-primary" />
          Métricas de Rendimiento del Broker
        </h2>
        <p className="text-sm text-muted-foreground">Tracking de metas, actividad, pipeline, ingresos y comisiones estimadas.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <ExecutiveMetricCard title="Tasa de Conversión" value={`${performance.conversion_rate || 0}%`} helper="Leads asignados que terminan en venta" icon={Target} tone="violet" />
        <ExecutiveMetricCard title="Leads Asignados" value={performance.total_assigned_leads || 0} helper={`${performance.contacted_leads || 0} contactados`} icon={Users} tone="blue" />
        <ExecutiveMetricCard title="Ingresos del Mes" value={formatCurrency(performance.total_revenue)} helper={`${performance.monthly_sales || 0} ventas cerradas`} icon={DollarSign} tone="emerald" />
        <ExecutiveMetricCard title="Comisión Estimada" value={formatCurrency(performance.estimated_commission)} helper={`${performance.total_points || 0} puntos acumulados`} icon={Trophy} tone="amber" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
        <Card className="xl:col-span-2">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Actividad en Tiempo Real
            </CardTitle>
            <CardDescription>Acciones recientes, prospectos calificados e intención del lead.</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="space-y-3">
              {feed.map((item) => (
                <div key={item.id || `${item.lead_name}-${item.created_at}`} className="flex items-start justify-between gap-3 rounded-xl border border-border/70 p-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{item.lead_name}</p>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge variant="secondary">{item.activity_type || 'actividad'}</Badge>
                      <Badge variant={item.intent_level === 'Alta' ? 'default' : 'secondary'}>Intención {item.intent_level}</Badge>
                    </div>
                  </div>
                  {item.points_earned > 0 && (
                    <Badge className="bg-primary text-white">+{item.points_earned} pts</Badge>
                  )}
                </div>
              ))}
              {feed.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Aún no hay actividad reciente para este broker.</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <LineChart className="w-5 h-5 text-primary" />
              Estado del Embudo
            </CardTitle>
            <CardDescription>Prospectos por etapa clave.</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <div className="space-y-3">
              {funnelStages.map(([key, label]) => (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{label}</span>
                    <Badge variant={key === 'venta' ? 'default' : 'outline'}>{pipeline[key] || 0}</Badge>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${((pipeline[key] || 0) / maxStage) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="cursor-pointer transition hover:shadow-md" onClick={() => onMetricClick('puntos')}>
          <CardContent className="p-4">
            <p className="text-sm font-medium">Desglose de puntos</p>
            <p className="text-xs text-muted-foreground">Cómo se ganaron los puntos.</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer transition hover:shadow-md" onClick={() => onMetricClick('apartados')}>
          <CardContent className="p-4">
            <p className="text-sm font-medium">Propiedades apartadas</p>
            <p className="text-xs text-muted-foreground">Lista reciente de apartados.</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer transition hover:shadow-md" onClick={() => onMetricClick('ventas')}>
          <CardContent className="p-4">
            <p className="text-sm font-medium">Acumulado de venta</p>
            <p className="text-xs text-muted-foreground">Ventas cerradas y monto total.</p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// KPI Detail Modal
const KPIDetailModal = ({ isOpen, onClose, type, data, stats }) => {
  if (!type) return null;

  const getModalContent = () => {
    switch (type) {
      case 'puntos':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-primary">{stats?.total_points || 0}</p>
                  <p className="text-xs text-muted-foreground">Puntos Totales</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{stats?.points_goal || 350}</p>
                  <p className="text-xs text-muted-foreground">Meta Mensual</p>
                </CardContent>
              </Card>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                Desglose de Puntos
              </h4>
              <div className="space-y-2">
                {data?.points_breakdown?.length > 0 ? (
                  data.points_breakdown.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg ${item.color || 'bg-primary'} flex items-center justify-center`}>
                          {React.createElement(activityIcons[item.type] || Activity, { className: "w-4 h-4 text-white" })}
                        </div>
                        <div>
                          <p className="text-sm font-medium capitalize">{item.type}</p>
                          <p className="text-xs text-muted-foreground">{item.count} acciones</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="font-bold">+{item.points} pts</Badge>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No hay datos de puntos este mes</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'apartados':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-secondary">{stats?.apartados || 0}</p>
                  <p className="text-xs text-muted-foreground">Apartados Este Mes</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{stats?.apartados_goal || 10}</p>
                  <p className="text-xs text-muted-foreground">Meta Mensual</p>
                </CardContent>
              </Card>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Bookmark className="w-4 h-4 text-secondary" />
                Apartados Recientes
              </h4>
              <ScrollArea className="h-[250px]">
                <div className="space-y-2">
                  {data?.apartados_list?.length > 0 ? (
                    data.apartados_list.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-secondary/20 text-secondary">
                              {item.lead_name?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{item.lead_name}</p>
                            <p className="text-xs text-muted-foreground">{item.property || 'Propiedad'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-secondary">${item.amount?.toLocaleString() || '0'}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.date ? format(new Date(item.date), "d MMM", { locale: es }) : ''}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No hay apartados este mes</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        );

      case 'ventas':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-accent">{stats?.ventas || 0}</p>
                  <p className="text-xs text-muted-foreground">Ventas</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{stats?.ventas_goal || 5}</p>
                  <p className="text-xs text-muted-foreground">Meta</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-lg font-bold text-green-500">${(data?.ventas_total || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Total MXN</p>
                </CardContent>
              </Card>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-accent" />
                Ventas Cerradas
              </h4>
              <ScrollArea className="h-[250px]">
                <div className="space-y-2">
                  {data?.ventas_list?.length > 0 ? (
                    data.ventas_list.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                            <ShoppingCart className="w-5 h-5 text-accent" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{item.lead_name}</p>
                            <p className="text-xs text-muted-foreground">{item.property || 'Propiedad'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-green-500">${item.amount?.toLocaleString() || '0'}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.date ? format(new Date(item.date), "d MMM yyyy", { locale: es }) : ''}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No hay ventas cerradas este mes</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        );

      case 'brokers':
        return (
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-emerald-500">{stats?.brokers_activos || 0}</p>
                <p className="text-xs text-muted-foreground">Brokers Activos</p>
              </CardContent>
            </Card>
            
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-500" />
                Rendimiento del Equipo
              </h4>
              <ScrollArea className="h-[280px]">
                <div className="space-y-2">
                  {data?.brokers_list?.length > 0 ? (
                    data.brokers_list.map((broker, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={broker.avatar} />
                            <AvatarFallback className="bg-emerald-500/20 text-emerald-500">
                              {broker.name?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{broker.name}</p>
                            <p className="text-xs text-muted-foreground">{broker.leads_count || 0} leads activos</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-primary text-white">{broker.points || 0} pts</Badge>
                          <p className="text-xs text-muted-foreground mt-1">{broker.ventas || 0} ventas</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No hay datos de brokers</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        );

      default:
        return <p>Sin datos disponibles</p>;
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'puntos': return 'Desglose de Puntos';
      case 'apartados': return 'Detalle de Apartados';
      case 'ventas': return 'Detalle de Ventas';
      case 'brokers': return 'Equipo de Brokers';
      default: return 'Detalle';
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'puntos': return 'Resumen de puntos ganados este mes';
      case 'apartados': return 'Propiedades apartadas este mes';
      case 'ventas': return 'Ventas cerradas y comisiones';
      case 'brokers': return 'Rendimiento del equipo';
      default: return '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {type === 'puntos' && <Trophy className="w-5 h-5 text-primary" />}
            {type === 'apartados' && <Bookmark className="w-5 h-5 text-secondary" />}
            {type === 'ventas' && <ShoppingCart className="w-5 h-5 text-accent" />}
            {type === 'brokers' && <Users className="w-5 h-5 text-emerald-500" />}
            {getTitle()}
          </DialogTitle>
          <DialogDescription>{getDescription()}</DialogDescription>
        </DialogHeader>
        {getModalContent()}
      </DialogContent>
    </Dialog>
  );
};

const LeaderboardItem = ({ broker, rank }) => {
  const getMedalColor = () => {
    if (rank === 1) return 'text-yellow-500';
    if (rank === 2) return 'text-gray-400';
    if (rank === 3) return 'text-orange-400';
    return 'text-muted-foreground';
  };

  const getMedalIcon = () => {
    if (rank <= 3) return Crown;
    return Medal;
  };

  const MedalIcon = getMedalIcon();

  return (
    <div className={`flex items-center gap-4 p-3 rounded-xl transition-colors ${
      rank <= 3 ? 'bg-muted/50' : 'hover:bg-muted/30'
    }`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getMedalColor()}`}>
        {rank <= 3 ? (
          <MedalIcon className="w-5 h-5" />
        ) : (
          <span className="text-sm font-medium">{rank}</span>
        )}
      </div>
      <Avatar className="w-10 h-10">
        <AvatarImage src={broker.avatar_url} />
        <AvatarFallback className="bg-primary/20 text-primary">
          {broker.broker_name?.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{broker.broker_name}</p>
        <p className="text-xs text-muted-foreground">{broker.ventas} ventas</p>
      </div>
      <div className="text-right">
        <p className="font-bold text-primary">{broker.total_points}</p>
        <p className="text-xs text-muted-foreground">puntos</p>
      </div>
    </div>
  );
};

const ActivityItem = ({ activity }) => {
  const Icon = activityIcons[activity.activity_type] || Activity;
  
  return (
    <div className="flex items-start gap-3 py-3">
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm">
          <span className="font-medium">{activity.broker_name}</span>
          {' '}{activity.activity_type === 'llamada' && 'llamó a'}
          {activity.activity_type === 'whatsapp' && 'envió WhatsApp a'}
          {activity.activity_type === 'email' && 'envió email a'}
          {activity.activity_type === 'zoom' && 'presentó por Zoom a'}
          {activity.activity_type === 'visita' && 'visitó con'}
          {activity.activity_type === 'apartado' && 'apartó con'}
          {activity.activity_type === 'venta' && 'cerró venta con'}{' '}
          <span className="font-medium">{activity.lead_name}</span>
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {activity.outcome || activity.description}
        </p>
      </div>
      {activity.points_earned > 0 && (
        <Badge variant="secondary" className="flex-shrink-0">
          +{activity.points_earned} pts
        </Badge>
      )}
    </div>
  );
};

const GamificationRule = ({ rule }) => {
  const icons = {
    phone: Phone,
    'message-circle': MessageCircle,
    mail: Mail,
    video: Video,
    'map-pin': MapPin,
    bookmark: Bookmark,
    trophy: Trophy,
    users: Users,
    star: Trophy,
  };
  const Icon = icons[rule.icon] || Trophy;

  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-accent" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium capitalize">{rule.action}</p>
        <p className="text-xs text-muted-foreground">{rule.description}</p>
      </div>
      <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
        +{rule.points} pts
      </Badge>
    </div>
  );
};

export const DashboardPage = () => {
  const { api, isIndividual } = useAuth();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activities, setActivities] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  // KPI Modal state
  const [kpiModal, setKpiModal] = useState({ open: false, type: null, data: null });
  const [loadingKpi, setLoadingKpi] = useState(false);

  // NUEVO: Real-time State
  const {
    stats: realTimeStats,
    leaderboard: realTimeLeaderboard,
    lastEvent,
    lastUpdate
  } = useDashboardRealTime();

  // NUEVO: Enhanced Dashboard Data
  const [trendsData, setTrendsData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [topBrokersData, setTopBrokersData] = useState(null);
  const [agencyExecutiveData, setAgencyExecutiveData] = useState(null);
  const [brokerPerformanceData, setBrokerPerformanceData] = useState(null);

  // Loading states
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [loadingFunnel, setLoadingFunnel] = useState(false);
  const [loadingTopBrokers, setLoadingTopBrokers] = useState(false);
  const [loadingAgencyExecutive, setLoadingAgencyExecutive] = useState(false);
  const [loadingBrokerPerformance, setLoadingBrokerPerformance] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const requests = [
        api.get('/dashboard/stats'),
        api.get('/dashboard/recent-activity?limit=10'),
        api.get('/gamification/rules'),
      ];

      // Only load leaderboard for agency users
      if (!isIndividual) {
        requests.push(api.get('/dashboard/leaderboard'));
      }

      const results = await Promise.all(requests);
      setStats(results[0].data);
      setActivities(results[1].data);
      setRules(results[2].data);
      if (!isIndividual && results[3]) {
        setLeaderboard(results[3].data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // NUEVO: Fetch Enhanced Dashboard Data
  // TEMPORAL: Deshabilitado hasta que backend implemente endpoints
  useEffect(() => {
    fetchTrendsData();
    fetchComparisonData();
    fetchFunnelData();
    if (!isIndividual) {
      fetchAgencyExecutiveData();
    } else {
      fetchBrokerPerformanceData();
    }
    console.log('Enhanced dashboard endpoints cargando...');
  }, []);

  // NUEVO: Fetch Trends
  const fetchTrendsData = async () => {
    setLoadingTrends(true);
    try {
      const response = await api.get('/dashboard/trends?months=6');
      setTrendsData(response.data);
    } catch (error) {
      console.log('Endpoint trends no disponible - usando datos dummy');
      setTrendsData({
        ventas_por_mes: [
          { mes: 'Enero', ventas: 12, monto: 4500000 },
          { mes: 'Febrero', ventas: 15, monto: 5200000 },
          { mes: 'Marzo', ventas: 8, monto: 3800000 },
          { mes: 'Abril', ventas: 18, monto: 6100000 },
          { mes: 'Mayo', ventas: 20, monto: 6500000 },
          { mes: 'Junio', ventas: 14, monto: 4900000 }
        ],
        leads_por_fuente: [
          { fuente: 'Facebook Ads', count: 45 },
          { fuente: 'Instagram', count: 32 },
          { fuente: 'Google Ads', count: 28 },
          { fuente: 'Referido', count: 18 },
          { fuente: 'Web', count: 12 }
        ]
      });
    } finally {
      setLoadingTrends(false);
    }
  };

  // NUEVO: Fetch Comparison
  const fetchComparisonData = async () => {
    setLoadingComparison(true);
    try {
      const response = await api.get('/dashboard/comparison');
      setComparisonData(response.data);
    } catch (error) {
      console.log('Endpoint comparison no disponible - usando datos dummy');
      setComparisonData({
        mes_actual: { ventas: 14, apartados: 5, leads_nuevos: 42 },
        mes_anterior: { ventas: 18, apartados: 6, leads_nuevos: 38 },
        cambio_porcentual: { ventas: '-22%', apartados: '-17%', leads_nuevos: '+11%' }
      });
    } finally {
      setLoadingComparison(false);
    }
  };

  // NUEVO: Fetch Funnel (usa trends data que incluye conversion_funnel)
  const fetchFunnelData = async () => {
    setLoadingFunnel(true);
    try {
      const response = await api.get('/dashboard/trends?months=6');
      setFunnelData({ conversion_funnel: response.data.conversion_funnel });
    } catch (error) {
      console.log('Endpoint funnel no disponible - usando datos dummy');
      setFunnelData({
        conversion_funnel: {
          nuevo: 45,
          contactado: 32,
          calificacion: 24,
          presentacion: 18,
          apartado: 8,
          venta: 5
        }
      });
    } finally {
      setLoadingFunnel(false);
    }
  };

  const fetchAgencyExecutiveData = async () => {
    setLoadingAgencyExecutive(true);
    try {
      const response = await api.get('/dashboard/agency-executive');
      setAgencyExecutiveData(response.data);
    } catch (error) {
      console.error('Error loading agency executive dashboard:', error);
      setAgencyExecutiveData(null);
    } finally {
      setLoadingAgencyExecutive(false);
    }
  };

  const fetchBrokerPerformanceData = async () => {
    setLoadingBrokerPerformance(true);
    try {
      const response = await api.get('/dashboard/broker-performance-overview');
      setBrokerPerformanceData(response.data);
    } catch (error) {
      console.error('Error loading broker performance dashboard:', error);
      setBrokerPerformanceData(null);
    } finally {
      setLoadingBrokerPerformance(false);
    }
  };

  // NUEVO: Update stats when WebSocket message arrives
  useEffect(() => {
    if (realTimeStats && lastEvent?.type === 'metrics_updated') {
      console.log('Stats actualizados via WebSocket:', realTimeStats);
      setStats(realTimeStats);
    }
  }, [realTimeStats, lastEvent]);

  // NUEVO: Update leaderboard when WebSocket message arrives
  useEffect(() => {
    if (realTimeLeaderboard && lastEvent?.type === 'leaderboard_changed') {
      console.log('Leaderboard actualizado via WebSocket:', realTimeLeaderboard);
      setLeaderboard(realTimeLeaderboard);
    }
  }, [realTimeLeaderboard, lastEvent]);

  // NUEVO: Refresh related dashboard panels when lead/import events arrive
  useEffect(() => {
    if (!lastEvent?.type || !DASHBOARD_RELOAD_EVENTS.has(lastEvent.type)) {
      return;
    }

    loadDashboard();

    if (lastEvent.type === 'import_completed') {
      fetchTrendsData();
      fetchComparisonData();
      fetchFunnelData();
      if (!isIndividual) {
        fetchAgencyExecutiveData();
      } else {
        fetchBrokerPerformanceData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEvent]);

  const handleKpiClick = async (type) => {
    setLoadingKpi(true);
    try {
      const res = await api.get(`/dashboard/kpi-detail/${type}`);
      setKpiModal({ open: true, type, data: res.data });
    } catch (error) {
      console.error('Error loading KPI detail:', error);
      // Show modal with empty data if endpoint doesn't exist yet
      setKpiModal({ open: true, type, data: {} });
    } finally {
      setLoadingKpi(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-4 sm:p-6">
                <Skeleton className="h-16 sm:h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit']">Dashboard</h1>
        <p className="text-sm sm:text-base text-muted-foreground">Tu resumen de rendimiento • Click en las tarjetas para ver detalles</p>
      </div>

      {/* Stats Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${isIndividual ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-3 sm:gap-4 lg:gap-6`}>
        <StatCard
          title="Puntos del Mes"
          value={stats?.total_points || 0}
          goal={stats?.points_goal}
          icon={Trophy}
          color="bg-primary"
          progress={stats?.points_progress}
          onClick={() => handleKpiClick('puntos')}
        />
        <StatCard
          title="Apartados"
          value={stats?.apartados || 0}
          goal={stats?.apartados_goal}
          icon={Bookmark}
          color="bg-secondary"
          progress={(stats?.apartados / stats?.apartados_goal) * 100}
          onClick={() => handleKpiClick('apartados')}
        />
        <StatCard
          title="Ventas Cerradas"
          value={stats?.ventas || 0}
          goal={stats?.ventas_goal}
          icon={ShoppingCart}
          color="bg-accent"
          progress={(stats?.ventas / stats?.ventas_goal) * 100}
          onClick={() => handleKpiClick('ventas')}
        />
        {!isIndividual && (
          <StatCard
            title="Brokers Activos"
            value={stats?.brokers_activos || 0}
            icon={Users}
            color="bg-emerald-600"
            onClick={() => handleKpiClick('brokers')}
          />
        )}
      </div>

      {!isIndividual && (
        <AgencyExecutiveDashboard
          data={agencyExecutiveData}
          loading={loadingAgencyExecutive}
        />
      )}

      {isIndividual && (
        <BrokerPerformanceDashboard
          data={brokerPerformanceData}
          loading={loadingBrokerPerformance}
          onMetricClick={handleKpiClick}
        />
      )}

      {/* NUEVA SECCIÓN: Dashboard Enhanced */}
      <div className="mt-8">
        <h2 className="text-2xl font-bold font-['Outfit'] mb-6 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-primary" />
          Analytics Avanzado
        </h2>

        {/* Real-time Indicator */}
        <div className="flex items-center gap-2 mb-4">
          <div className={`h-2 w-2 rounded-full ${lastUpdate ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
          <span className="text-sm text-muted-foreground">
            {lastUpdate ? 'Actualizado en tiempo real' : 'Conectando...'}
          </span>
        </div>

        {/* Enhanced Components Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Trends Chart */}
          <div>
            <TrendsChart
              data={trendsData?.ventas_por_mes || []}
              months={6}
              loading={loadingTrends}
            />
          </div>

          {/* Comparison Card */}
          <div>
            <ComparisonCard
              data={comparisonData}
              loading={loadingComparison}
            />
          </div>

          {/* Conversion Funnel */}
          <div>
            <ConversionFunnel
              data={funnelData?.conversion_funnel || {}}
              loading={loadingFunnel}
            />
          </div>

          {/* Leads by Source */}
          <div>
            <LeadsBySource
              data={trendsData?.leads_por_fuente || []}
              loading={loadingTrends}
            />
          </div>
        </div>

        {/* Top Brokers */}
        {!isIndividual && (
          <div className="mt-6">
            <TopBrokersList
              metric="ventas"
              limit={5}
              data={topBrokersData}
              loading={loadingTopBrokers}
            />
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className={`grid grid-cols-1 ${isIndividual ? 'lg:grid-cols-2' : 'lg:grid-cols-3'} gap-4 sm:gap-6`}>
        {/* Leaderboard - Only for Agency */}
        {!isIndividual && (
          <Card className="lg:col-span-1">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                Leaderboard Mensual
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">Top brokers por puntos</CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
              <ScrollArea className="h-[280px] sm:h-[320px] pr-4">
                <div className="space-y-2">
                  {leaderboard.map((broker, idx) => (
                    <LeaderboardItem key={broker.broker_id} broker={broker} rank={idx + 1} />
                  ))}
                  {leaderboard.length === 0 && (
                    <p className="text-center text-muted-foreground py-8 text-sm">
                      No hay datos de leaderboard
                    </p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Recent Activity */}
        <Card className="lg:col-span-1">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              Actividad Reciente
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {isIndividual ? 'Tus últimas acciones' : 'Últimas acciones del equipo'}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <ScrollArea className="h-[280px] sm:h-[320px] pr-4">
              <div className="divide-y divide-border">
                {activities.map((activity) => (
                  <ActivityItem key={activity.id} activity={activity} />
                ))}
                {activities.length === 0 && (
                  <p className="text-center text-muted-foreground py-8 text-sm">
                    No hay actividad reciente
                  </p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Gamification Rules */}
        <Card className="lg:col-span-1">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
              Sistema de Puntos
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Gana puntos por cada acción</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <ScrollArea className="h-[280px] sm:h-[320px] pr-4">
              <div className="space-y-1">
                {rules.map((rule) => (
                  <GamificationRule key={rule.id} rule={rule} />
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Rate & Goals Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle>Tasa de Conversión</CardTitle>
            <CardDescription>Leads que se convierten en ventas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    className="text-muted stroke-current"
                    strokeWidth="8"
                    fill="transparent"
                    r="56"
                    cx="64"
                    cy="64"
                  />
                  <circle
                    className="text-primary stroke-current"
                    strokeWidth="8"
                    strokeLinecap="round"
                    fill="transparent"
                    r="56"
                    cx="64"
                    cy="64"
                    strokeDasharray={`${(stats?.conversion_rate || 0) * 3.52} 352`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold">{stats?.conversion_rate || 0}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Resumen de Leads</CardTitle>
            <CardDescription>Estado actual del pipeline</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <span className="text-sm">Leads Nuevos</span>
                <Badge variant="outline">{stats?.leads_nuevos || 0}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">En Proceso</span>
                <Badge variant="outline">{(stats?.apartados || 0) + (stats?.leads_nuevos || 0)}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Ventas Cerradas</span>
                <Badge className="bg-primary">{stats?.ventas || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Detail Modal */}
      <KPIDetailModal
        isOpen={kpiModal.open}
        onClose={() => setKpiModal({ open: false, type: null, data: null })}
        type={kpiModal.type}
        data={kpiModal.data}
        stats={stats}
      />
    </div>
  );
};
